import React, { useMemo, useState } from 'react'
import { useExpense } from '../contexts/ExpenseContext'
import { Plus, Target, Edit3, Trash2, IndianRupee, Layers } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { formatRupees } from '../utils/currency'

const BudgetsPage = () => {
  const {
    budgets,
    expenses,
    loading,
    addBudget,
    updateBudget,
    deleteBudget,
    budgetMeta
  } = useExpense()

  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingBudget, setEditingBudget] = useState(null)
  const [formData, setFormData] = useState({
    category: 'Food',
    custom_category: '',
    amount: '',
    period: 'monthly',
    source_type: 'monthly',
    source_name: ''
  })

  const categories = [
    'Food',
    'Rent',
    'Groceries',
    'Transportation',
    'Entertainment',
    'Healthcare',
    'Shopping',
    'Bills',
    'Utilities',
    'Education',
    'Other'
  ]

  const toMonthlyAmount = (amount, period = 'monthly') => {
    const value = parseFloat(amount) || 0
    if (period === 'weekly') return value * 4.33
    if (period === 'yearly') return value / 12
    return value
  }

  const categoryBudgetCount = useMemo(() => {
    return budgets.reduce((acc, budget) => {
      acc[budget.category] = (acc[budget.category] || 0) + 1
      return acc
    }, {})
  }, [budgets])

  const linkedSpentByBudgetId = useMemo(() => {
    return expenses.reduce((acc, expense) => {
      if (!expense.budget_id) return acc
      const key = expense.budget_id
      acc[key] = (acc[key] || 0) + (parseFloat(expense.amount) || 0)
      return acc
    }, {})
  }, [expenses])

  const unlinkedSpentByCategory = useMemo(() => {
    return expenses.reduce((acc, expense) => {
      if (expense.budget_id) return acc
      const category = expense.category || 'Other'
      acc[category] = (acc[category] || 0) + (parseFloat(expense.amount) || 0)
      return acc
    }, {})
  }, [expenses])

  const extraSourceNames = useMemo(() => {
    const fromMeta = (budgetMeta?.extraSources || []).map(source => source.name).filter(Boolean)
    const fromBudgets = budgets
      .filter(budget => (budget.source_type || 'monthly') === 'extra' && budget.source_name)
      .map(budget => budget.source_name)

    return [...new Set([...fromMeta, ...fromBudgets])]
  }, [budgetMeta?.extraSources, budgets])

  const fixedMonthlyBudget = parseFloat(budgetMeta?.monthlyBudget || 0)
  const monthlyAllocated = parseFloat(budgetMeta?.monthlyAllocated || 0)
  const monthlyRemaining = parseFloat(budgetMeta?.monthlyRemaining ?? (fixedMonthlyBudget - monthlyAllocated))

  const totalMonthlyEquivalent = budgets.reduce(
    (total, budget) => total + toMonthlyAmount(budget.amount, budget.period),
    0
  )

  const extraMonthlyEquivalent = budgets
    .filter(budget => (budget.source_type || 'monthly') === 'extra')
    .reduce((total, budget) => total + toMonthlyAmount(budget.amount, budget.period), 0)

  const canAllocateMonthly = fixedMonthlyBudget > 0

  const handleAddBudget = async (e) => {
    e.preventDefault()

    const resolvedCategory = formData.category === 'Other'
      ? formData.custom_category.trim()
      : formData.category

    if (!resolvedCategory) {
      return
    }

    if (formData.source_type === 'extra' && !formData.source_name.trim()) {
      return
    }

    try {
      await addBudget({
        category: resolvedCategory,
        amount: parseFloat(formData.amount),
        period: formData.period,
        source_type: formData.source_type,
        source_name: formData.source_type === 'extra' ? formData.source_name.trim() : null
      })
      
      setFormData({
        category: 'Food',
        custom_category: '',
        amount: '',
        period: 'monthly',
        source_type: 'monthly',
        source_name: ''
      })
      setShowAddModal(false)
    } catch (error) {
      console.error('Error adding budget:', error)
    }
  }

  const handleEditBudget = async (e) => {
    e.preventDefault()

    const resolvedCategory = formData.category === 'Other'
      ? formData.custom_category.trim()
      : formData.category

    if (!resolvedCategory) {
      return
    }

    if (formData.source_type === 'extra' && !formData.source_name.trim()) {
      return
    }

    try {
      await updateBudget(editingBudget.id, {
        category: resolvedCategory,
        amount: parseFloat(formData.amount),
        period: formData.period,
        source_type: formData.source_type,
        source_name: formData.source_type === 'extra' ? formData.source_name.trim() : null
      })
      
      setShowEditModal(false)
      setEditingBudget(null)
    } catch (error) {
      console.error('Error updating budget:', error)
    }
  }

  const handleDeleteBudget = async (budgetId) => {
    if (window.confirm('Are you sure you want to delete this budget?')) {
      try {
        await deleteBudget(budgetId)
      } catch (error) {
        console.error('Error deleting budget:', error)
      }
    }
  }

  const startEditBudget = (budget) => {
    const isStandardCategory = categories.includes(budget.category) && budget.category !== 'Other'

    setEditingBudget(budget)
    setFormData({
      category: isStandardCategory ? budget.category : 'Other',
      custom_category: isStandardCategory ? '' : (budget.category || ''),
      amount: budget.amount.toString(),
      period: budget.period,
      source_type: budget.source_type || 'monthly',
      source_name: budget.source_name || ''
    })
    setShowEditModal(true)
  }

  const getBudgetProgress = (budget) => {
    const linkedSpent = linkedSpentByBudgetId[budget.id] || 0

    let fallbackCategorySpent = 0
    if ((categoryBudgetCount[budget.category] || 0) === 1) {
      fallbackCategorySpent = unlinkedSpentByCategory[budget.category] || 0
    }

    const spent = linkedSpent + fallbackCategorySpent
    const percentage = budget.amount > 0 ? (spent / budget.amount) * 100 : 0

    return {
      spent,
      percentage: Math.min(percentage, 100),
      remaining: Math.max(budget.amount - spent, 0)
    }
  }

  const getSourceLabel = (budget) => {
    const sourceType = budget.source_type || 'monthly'
    if (sourceType === 'extra') {
      return `Extra • ${budget.source_name || 'Unnamed source'}`
    }
    return 'Monthly Budget'
  }

  if (loading) {
    return <LoadingSpinner message="Loading budgets..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Budgets
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Allocate fixed monthly budget and track extra budgets separately
          </p>
        </div>
        
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Budget</span>
        </button>
      </div>

      {!canAllocateMonthly && (
        <div className="card border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Set your fixed monthly budget in <strong>Profile</strong> first. After that, allocate category budgets from the monthly pool here.
          </p>
        </div>
      )}

      {/* Total Budget Summary */}
      {budgets.length > 0 && (
        <div className="card bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Budget Allocation Overview
              </h2>
              <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                {formatRupees(totalMonthlyEquivalent)} / {formatRupees(fixedMonthlyBudget || totalMonthlyEquivalent)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Across {budgets.length} allocation{budgets.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-3">
              <IndianRupee className="h-14 w-14 text-primary-600 dark:text-primary-400 opacity-30" />
              <Layers className="h-14 w-14 text-primary-600 dark:text-primary-400 opacity-30" />
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-primary-200 dark:border-primary-700">
            <div className="text-center">
              <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {formatRupees(fixedMonthlyBudget)}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Fixed Monthly Budget</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatRupees(monthlyAllocated)}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Allocated from Monthly</p>
            </div>
            <div className="text-center">
              <p className={`text-2xl font-bold ${monthlyRemaining >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>
                {formatRupees(monthlyRemaining)}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Monthly Remaining</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {formatRupees(extraMonthlyEquivalent)}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Extra Budgets (Monthly Eq.)</p>
            </div>
          </div>
        </div>
      )}

      {/* Budgets List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {budgets.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Target className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No budgets set
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first budget to start tracking your spending limits
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              Create Budget
            </button>
          </div>
        ) : (
          budgets.map((budget) => {
            const progress = getBudgetProgress(budget)
            return (
              <div key={budget.id} className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {budget.category}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400 capitalize hidden sm:inline">
                      {budget.period}
                    </span>
                    <button
                      onClick={() => startEditBudget(budget)}
                      className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                      title="Edit budget"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteBudget(budget.id)}
                      className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      title="Delete budget"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mb-4 flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    (budget.source_type || 'monthly') === 'extra'
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                  }`}>
                    {getSourceLabel(budget)}
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Spent</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatRupees(progress.spent)} / {formatRupees(budget.amount)}
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-300 ${
                        progress.percentage > 90 
                          ? 'bg-red-500' 
                          : progress.percentage > 70 
                          ? 'bg-yellow-500' 
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${progress.percentage}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                    <span>{progress.percentage.toFixed(1)}% used</span>
                    <span>{formatRupees(progress.remaining)} remaining</span>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Add Budget Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Create Budget
            </h2>
            
            <form onSubmit={handleAddBudget} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    category: e.target.value,
                    custom_category: e.target.value === 'Other' ? prev.custom_category : ''
                  }))}
                  className="input-field"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {formData.category === 'Other' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Custom Category Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.custom_category}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_category: e.target.value }))}
                    className="input-field"
                    placeholder="e.g. Pet Care"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Budget Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className="input-field"
                  placeholder="Enter amount in ₹"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Period
                </label>
                <select
                  value={formData.period}
                  onChange={(e) => setFormData(prev => ({ ...prev, period: e.target.value }))}
                  className="input-field"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Budget Source
                </label>
                <select
                  value={formData.source_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, source_type: e.target.value, source_name: e.target.value === 'monthly' ? '' : prev.source_name }))}
                  className="input-field"
                >
                  <option value="monthly">From Monthly Budget</option>
                  <option value="extra">From Extra Budget</option>
                </select>
              </div>

              {formData.source_type === 'extra' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Extra Budget Source Name
                  </label>
                  <input
                    type="text"
                    list="extra-budget-sources"
                    required
                    value={formData.source_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, source_name: e.target.value }))}
                    className="input-field"
                    placeholder="e.g. Freelance Bonus Apr"
                  />
                  <datalist id="extra-budget-sources">
                    {extraSourceNames.map(name => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Pick an existing extra source or type a new one.
                  </p>
                </div>
              )}

              {formData.source_type === 'monthly' && (
                <div className="text-xs rounded-lg p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200">
                  Monthly remaining to allocate: <strong>{formatRupees(monthlyRemaining)}</strong>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formData.source_type === 'monthly' && !canAllocateMonthly}
                  className="flex-1 btn-primary"
                >
                  Create Budget
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Budget Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Edit Budget
            </h2>
            
            <form onSubmit={handleEditBudget} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    category: e.target.value,
                    custom_category: e.target.value === 'Other' ? prev.custom_category : ''
                  }))}
                  className="input-field"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {formData.category === 'Other' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Custom Category Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.custom_category}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_category: e.target.value }))}
                    className="input-field"
                    placeholder="e.g. Family Events"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Budget Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className="input-field"
                  placeholder="Enter amount in ₹"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Period
                </label>
                <select
                  value={formData.period}
                  onChange={(e) => setFormData(prev => ({ ...prev, period: e.target.value }))}
                  className="input-field"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Budget Source
                </label>
                <select
                  value={formData.source_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, source_type: e.target.value, source_name: e.target.value === 'monthly' ? '' : prev.source_name }))}
                  className="input-field"
                >
                  <option value="monthly">From Monthly Budget</option>
                  <option value="extra">From Extra Budget</option>
                </select>
              </div>

              {formData.source_type === 'extra' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Extra Budget Source Name
                  </label>
                  <input
                    type="text"
                    list="extra-budget-sources-edit"
                    required
                    value={formData.source_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, source_name: e.target.value }))}
                    className="input-field"
                    placeholder="e.g. Side Hustle Income"
                  />
                  <datalist id="extra-budget-sources-edit">
                    {extraSourceNames.map(name => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                </div>
              )}

              {formData.source_type === 'monthly' && (
                <div className="text-xs rounded-lg p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200">
                  Monthly remaining to allocate: <strong>{formatRupees(monthlyRemaining)}</strong>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingBudget(null)
                  }}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formData.source_type === 'monthly' && !canAllocateMonthly}
                  className="flex-1 btn-primary"
                >
                  Update Budget
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default BudgetsPage
